import { htmlConfig } from './html-config';
import { usesBearerSession } from './native/bearer-session';
import { isNativeShell } from './native/platform';
import { ensureTokenLoaded, getNativeToken } from './native/token';
import { stampReloadReason } from './telemetry/page-reload';

export function getApiBase(): string {
  if (isNativeShell()) {
    // Native shell has no reverse proxy — hit the backend at an absolute URL.
    // Configurable per build (dev: local API; later: staging/prod); the default
    // targets the local dev API, which the iOS simulator reaches via the shared
    // host loopback.
    return htmlConfig('native-api-base-url') || 'http://localhost:8000';
  }
  // Empty = relative paths; Traefik routes to the API in prod/staging.
  // `VITE_API_BASE_URL` is only for local dev (the Vite dev proxy also covers it).
  return import.meta.env.VITE_API_BASE_URL || '';
}

export function getLoginUrl(): string {
  return `${getApiBase()}/auth/login`;
}

/**
 * Wipes every user-scoped piece of client state: all Zustand stores (chat
 * list, agents, skills, budget, session, streaming, feature-flag cache) plus
 * the per-tab tenant selection in ``sessionStorage``.
 *
 * MUST run on every auth boundary (logout, 401 bounce) so the *previous*
 * user's data cannot survive a logout→login on a shared browser. The chat
 * list lives purely in the in-memory Zustand heap, which the browser can
 * reuse across the redirect cycle — notably via the bfcache, which restores
 * the JS heap without re-running React mount effects. See also the
 * ``BfcacheGuard`` in ``app-shell-runtime.tsx`` for the bfcache-restore case.
 */
async function clearClientSession(opts?: { clearNativeCredential?: boolean }): Promise<void> {
  if (typeof window === 'undefined') return;
  try {
    // Dynamic import to avoid a circular dependency (stores import api.ts).
    const { resetUserStores } = await import('./stores/reset-stores');
    resetUserStores();
  } catch {
    // Ignore if stores are not available (early teardown).
  }
  // Per-tab tenant context lives in sessionStorage, outside the stores.
  try {
    sessionStorage.removeItem('currentTenantId');
  } catch {
    // sessionStorage may be unavailable (private mode).
  }
  // The native app remembers the open chat and its unsent text on the device
  // (localStorage, so it outlives the webview). That is one person's content:
  // it must not be waiting in the composer for whoever signs in next.
  try {
    const { clearSessionRestore } = await import('./session-restore');
    clearSessionRestore();
  } catch {
    // Chunk unavailable during teardown — nothing else to do here.
  }

  // Bearer clients (the app, the Office pane): drop the stored session id ONLY
  // on an explicit logout.
  // A transient 401 bounce must NOT wipe it — a racing/stale 401 right after
  // login would otherwise destroy the freshly persisted token, forcing a
  // re-login on every app start (and re-opening the login browser). A fresh
  // login overwrites the keychain anyway, so keeping a possibly-stale token
  // across a 401 is safe (it is an opaque, server-revocable session id).
  if (opts?.clearNativeCredential && usesBearerSession()) {
    try {
      // Via nativeLogout, not a bare keychain wipe: the app also holds a
      // long-lived refresh token now, and that one has to be revoked at the
      // backend or it stays alive with nothing left to reach it.
      const { nativeLogout } = await import('./native/auth');
      await nativeLogout();
    } catch {
      // Ignore — nothing to clear.
    }
  }
}

/**
 * Latched once the 401 bounce has started, so only the first of N concurrent
 * failures navigates.
 *
 * ``window.location.assign`` does not halt JS execution: it schedules the
 * navigation and returns. A session that expires while several requests are
 * in flight (parallel store fetches, the budget/changelog pollers, the SSE
 * reconnect) therefore used to produce one ``assign`` call per failed request.
 * Each one starts its own OIDC round trip, and every ``/protocol/openid-connect/auth``
 * hit makes Keycloak issue a fresh ``AUTH_SESSION_ID``. The login form the user
 * ends up looking at then belongs to an authentication session that a later
 * redirect has already superseded, and submitting it fails with Keycloak's
 * ``loginTimeout`` ("Anmeldeversuch wegen Zeitüberschreitung abgebrochen") —
 * which is misleading, since nothing actually timed out.
 *
 * Not reset on purpose: the page is navigating away, and the module heap goes
 * with it. A bfcache restore would bring the latch back, but ``BfcacheGuard``
 * (app-shell-runtime.tsx) forces a reload on restore, which re-instantiates
 * this module.
 */
let loginRedirectStarted = false;

/** Claims the right to navigate to the login page. Returns false if another
 *  failed request already claimed it. */
function claimLoginNavigation(): boolean {
  if (loginRedirectStarted) return false;
  loginRedirectStarted = true;
  return true;
}

/**
 * Send the browser to the login page (native: start the system-browser flow;
 * the Office pane: show its sign-in screen, the dialog only opens on a click).
 *
 * Exported because the ``/me`` bootstrap does not go through ``fetchWithAuth``
 * and used to leave an expired session sitting on a page that could not
 * recover: every gated area showed a refusal and no route out (KAN-903).
 * SessionProvider calls this once the store reports ``unauthenticated``.
 */
export function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  if (usesBearerSession()) {
    // Native: a relative /login → /auth/login navigation would point into the
    // app bundle (dead end). Start the system-browser login flow instead. The
    // Office pane cannot navigate either: it is framed inside Outlook.
    // Deliberately NOT behind the one-shot latch: native never navigates away,
    // so the latch would never reset and a second session expiry within the
    // same app run could not reopen the login. startNativeLogin dedupes
    // concurrent triggers itself (loginInFlight + post-login cooldown).
    // After the wipe, not beside it: the pane answers by showing its sign-in
    // screen, which a wipe landing afterwards would turn back into a spinner.
    // .catch: a failed chunk load or login start would otherwise vanish as an
    // unhandled rejection — WKWebView logs nothing for those.
    void clearClientSession()
      .then(() => import('./native/auth'))
      .then((m) => m.startNativeLogin())
      .catch((err) => console.error('[native-auth] login bounce failed:', err));
    return;
  }
  if (!claimLoginNavigation()) return;
  // Best-effort eager wipe on a 401 bounce (session expiry that skips the
  // logout button). The reliable guarantee against cross-user leaks after a
  // re-login is the bfcache ``pageshow`` guard; this clears state where it can.
  void clearClientSession();
  // Immer auf die FE-Login-Seite weiterleiten, die den OIDC-Flow startet.
  window.location.assign('/login');
}

/**
 * Thrown by ``fetchWithAuth`` for non-2xx responses. Carries the HTTP
 * status plus the structured ``error.code``/``error.details`` from the
 * backend envelope (Story 24.5), so callers can dispatch on machine-
 * readable fields instead of matching localized message text.
 */
export class ApiHttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly details?: Record<string, unknown>,
    /**
     * The request's correlation id (``error.trace_id``). Where the backend
     * reported the failure to the error log, this is the id to find it by.
     */
    public readonly errorId?: string
  ) {
    super(message);
    this.name = 'ApiHttpError';
  }
}

/**
 * The headers every authenticated request carries: the workspace of this tab
 * and, in the native app, the session as a Bearer token. Shared by
 * ``fetchWithAuth`` and the upload of parts, which goes through
 * XMLHttpRequest for its progress events. Returns the token it sent, so a
 * caller can tell a stale one from the current one after a 401.
 */
export async function withAuthHeaders(
  base?: HeadersInit
): Promise<{ headers: Headers; nativeToken: string | null }> {
  // Add X-Tenant-Id header from sessionStorage (per-tab state). This is the
  // ONLY source of the header: whatever the workspace switcher shows is what
  // every request carries. Opening a chat or an agent that lives in another
  // workspace switches into it for real (see ``switchTabTenant`` in the chat
  // store) rather than shadowing the header with a second, invisible value —
  // which used to leave the switcher naming one workspace while the data came
  // from another (KAN-982).
  const headers = new Headers(base);
  if (typeof window !== 'undefined') {
    const tenantId = sessionStorage.getItem('currentTenantId');
    if (tenantId) {
      headers.set('X-Tenant-Id', tenantId);
    }
  }

  // The native app and the Office pane authenticate via Bearer (the opaque
  // session id); the web client relies on the httpOnly session cookie instead.
  // Wait for the keychain read: on cold start the request burst would otherwise
  // race ahead of it, go out without the header, 401, and pop the login
  // browser over a valid session.
  // Memoized — resolves synchronously once the token is in memory.
  let nativeToken: string | null = null;
  if (usesBearerSession()) {
    await ensureTokenLoaded();
    nativeToken = getNativeToken();
    if (nativeToken) headers.set('Authorization', `Bearer ${nativeToken}`);
  }
  return { headers, nativeToken };
}

interface ErrorEnvelope {
  detail?: unknown;
  code?: string;
  details?: Record<string, unknown>;
  error?: {
    code?: string;
    message?: unknown;
    details?: Record<string, unknown>;
    trace_id?: unknown;
  };
}

/**
 * The readable sentence of an error answer, never JSON. ``detail`` is an object
 * whenever the backend raised with code and message (``LICENSE_INSUFFICIENT``,
 * ``TENANT_FORBIDDEN``, …); its text is then in ``error.message``.
 */
function readableMessage(envelope: ErrorEnvelope, status: number): string {
  if (typeof envelope.detail === 'string' && envelope.detail) return envelope.detail;
  if (typeof envelope.error?.message === 'string' && envelope.error.message) {
    return envelope.error.message;
  }
  const nested = (envelope.detail as { message?: unknown } | null | undefined)?.message;
  if (typeof nested === 'string' && nested) return nested;
  return `Request fehlgeschlagen: ${status}`;
}

/**
 * The ``ApiHttpError`` for a non-2xx answer, read from the backend envelope
 * (``error.code``, ``error.details``, ``error.trace_id``).
 */
export function apiHttpErrorFrom(status: number, body: unknown): ApiHttpError {
  const envelope = (body ?? {}) as ErrorEnvelope;
  const code = envelope.error?.code ?? envelope.code;
  const details = envelope.error?.details ?? envelope.details;
  const errorId =
    typeof envelope.error?.trace_id === 'string' ? envelope.error.trace_id : undefined;
  return new ApiHttpError(readableMessage(envelope, status), status, code, details, errorId);
}

/**
 * Wrapper um fetch mit konsistenter 401-Handhabung.
 * - credentials default: 'include'
 * - Bei 401 optional Redirect auf /login (client), sonst Fehler werfen (SSR)
 * - Fügt X-Tenant-Id Header aus sessionStorage hinzu (per-Tab Tenant-State)
 */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init?: RequestInit & { redirectOn401?: boolean }
): Promise<Response> {
  const { redirectOn401 = true, ...rest } = init || {};
  const { headers, nativeToken: sentNativeToken } = await withAuthHeaders(rest.headers);

  let response = await fetch(input, {
    credentials: rest.credentials ?? 'include',
    ...rest,
    headers,
  });

  // Native: a 401 may be stale — the request went out with a token (or none)
  // that a re-login replaced while it was in flight (cold-start stragglers,
  // slow requests outliving the post-login cooldown). Retry once with the
  // current token instead of bouncing to the login browser over a valid
  // session. Bodies here are strings/FormData, both safely re-sendable.
  //
  // Failing that, the session itself is gone — most often because Redis, which
  // holds it, restarted (it runs as a cache with no persistence). The stored
  // refresh token buys a new one silently, the same way the browser re-mints
  // itself from its refresh cookie. Only if that is refused too does the user
  // see a login (KAN-316).
  let renewalWasTransient = false;
  if (response.status === 401 && usesBearerSession()) {
    let retryToken = getNativeToken();
    if (!retryToken || retryToken === sentNativeToken) {
      const { renewNativeSession } = await import('./native/auth');
      const renewal = await renewNativeSession();
      retryToken = renewal.sessionId;
      renewalWasTransient = renewal.transient;
    }
    if (retryToken && retryToken !== sentNativeToken) {
      headers.set('Authorization', `Bearer ${retryToken}`);
      response = await fetch(input, {
        credentials: rest.credentials ?? 'include',
        ...rest,
        headers,
      });
    }
  }

  if (response.status === 401) {
    // A renewal that could not be asked (offline, rate limited, backend down)
    // says nothing about whether the login is still good — opening the login
    // browser there would turn a blip into a visible sign-out for everyone.
    if (redirectOn401 && typeof window !== 'undefined' && !renewalWasTransient) {
      redirectToLogin();
    }
    throw new Error('Nicht authentifiziert (401)');
  }

  // Handle 403: disabled user or invalid tenant. Code-based dispatch
  // (Story 24.5) — was brittle ``body.detail === "<German>"`` before.
  if (response.status === 403 && typeof window !== 'undefined') {
    try {
      const body = await response.clone().json();
      const code: string | undefined = body?.error?.code ?? body?.code;
      if (code === 'ACCOUNT_DEACTIVATED') {
        // Shares the latch with the 401 bounce: a deactivated account fails
        // every in-flight request at once, and each extra navigation would
        // rotate Keycloak's AUTH_SESSION_ID out from under the login form.
        if (claimLoginNavigation()) {
          // Clear cookies and redirect to login
          document.cookie.split(';').forEach((c) => {
            document.cookie =
              c.trim().split('=')[0] + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
          });
          window.location.assign('/login');
        }
        throw new Error('ACCOUNT_DEACTIVATED');
      }
      if (code === 'TENANT_FORBIDDEN') {
        // Stored tenant is no longer accessible — clear it and reload
        // so the tenant store picks a valid tenant on next init.
        sessionStorage.removeItem('currentTenantId');
        stampReloadReason('tenant-forbidden');
        window.location.reload();
        throw new Error('TENANT_FORBIDDEN');
      }
    } catch (e) {
      if (
        e instanceof Error &&
        (e.message === 'ACCOUNT_DEACTIVATED' || e.message === 'TENANT_FORBIDDEN')
      ) {
        throw e;
      }
      // JSON parse failed, continue to normal error handling
    }
  }

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.clone().json();
    } catch {
      // Response body might not be JSON
    }
    throw apiHttpErrorFrom(response.status, body);
  }

  return response;
}

export function getMeUrl(): string {
  return `${getApiBase()}/api/v1/me`;
}

export async function logout(): Promise<void> {
  // Wipe all user-scoped client state (incl. the streaming store) BEFORE
  // navigating away, so nothing of this user survives into the next login on a
  // shared browser. This is an EXPLICIT
  // logout, so also drop the native keychain credential.
  await clearClientSession({ clearNativeCredential: true });

  // Backend macht den Keycloak-Redirect (GET /auth/logout)
  // Dadurch keine NEXT_PUBLIC_KEYCLOAK_* Variablen im Frontend nötig
  if (typeof window !== 'undefined') {
    if (usesBearerSession()) {
      // Native: the session id is already cleared by clearClientSession above.
      // Land on the NativeAuthGate (logged-out welcome screen) instead of
      // auto-opening the login browser: re-bootstrapping the profile yields a
      // 401 with no stored token, which flips the session store to
      // 'unauthenticated' and the gate takes over.
      void import('./stores/session.store')
        .then((m) => m.useSessionStore.getState().loadProfile({ force: true }))
        .catch(() => {});
      return;
    }
    window.location.assign('/auth/logout');
  }
}
